// proposal-batch-governance-prescan.ts — CLI 3.38.0-beta.2, security audit
// F-1 ("no code wins") on the F-72 retry classifier.
//
// `applyProposedChangesForAttempt` stops at the FIRST refusal. When a batch
// carried both a fixable error (stale revision, schema, batch collision…) and
// a governance violation, whichever check ran first decided the route: a
// fixable code won, the violation was never seen, and the attempt was retried
// instead of escalated. This scan runs every GOVERNANCE check over the WHOLE
// batch before any fixable one, so a violation anywhere in the batch always
// produces the uncoded (⇒ escalate, counted_failures untouched) refusal.
//
// It reuses the guards the applier runs — nothing new decides what is
// governance — and reads worker content defensively: a malformed field is
// skipped here (the schema check later refuses it as fixable), never thrown.
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import { quoteWorkerValue } from "./proposal-change-types.js";
import { checkExistingTicketChange } from "./proposal-existing-ticket-guards.js";
import { checkProposedScope } from "./proposal-scope-guard.js";
import { toApplierChange } from "./proposed-change-wire-mapping.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { ticketUnrunnableInV1Code, ticketUnrunnableInV1Reason } from "./ticket-v1-runnability.js";

type WireChange = AutopilotResult["proposed_changes"][number];
type Runnability = Pick<Ticket, "id" | "kind" | "executor" | "command_adapter">;

function contentOf(change: WireChange): Record<string, unknown> {
  const content = change.content as unknown;
  return typeof content === "object" && content !== null && !Array.isArray(content)
    ? (content as Record<string, unknown>)
    : {};
}

function stringsOf(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : undefined;
}

/** Only the string fields the runnability check reads; `undefined` when one is missing. */
function runnabilityOf(base: Partial<Ticket>, content: Record<string, unknown>) {
  const pick = (key: "kind" | "executor" | "command_adapter") =>
    typeof content[key] === "string" ? (content[key] as string) : base[key];
  const ticket = {
    id: base.id ?? "(mới)",
    kind: pick("kind"),
    executor: pick("executor"),
    command_adapter: pick("command_adapter"),
  };
  return Object.values(ticket).every((v) => typeof v === "string")
    ? (ticket as Runnability)
    : undefined;
}

/** `ticketUnrunnableInV1Reason` when it refuses WITHOUT a fixable code (deploy/revert, controller or bootstrap adapter). */
function uncodedUnrunnable(ticket: Runnability | undefined): string | undefined {
  if (ticket === undefined) return undefined;
  const reason = ticketUnrunnableInV1Reason(ticket);
  return reason !== undefined && ticketUnrunnableInV1Code(ticket) === undefined
    ? reason
    : undefined;
}

function changeGovernanceRefusal(change: WireChange, tickets: Ticket[]): string | undefined {
  const mapped = toApplierChange(change, tickets);
  if (!mapped.ok && mapped.code === undefined) return mapped.reason; // bootstrap kind at the wire
  const content = contentOf(change);
  const scope = checkProposedScope(stringsOf(content.allowed_paths));
  if (scope !== undefined) return scope;
  if (change.op === "create") {
    const id = mapped.ok ? mapped.change.new_ticket?.id : undefined;
    if (id !== undefined && isControllerReservedTicketId(id)) {
      return `ticket id ${id} dành cho controller (phiên review) — worker không được create`;
    }
    return uncodedUnrunnable(runnabilityOf({}, content));
  }
  if (isControllerReservedTicketId(change.target)) {
    return `ticket ${quoteWorkerValue(change.target)} dành cho controller (phiên review) — worker không được ${change.op}`;
  }
  const target = tickets.find((t) => t.id === change.target);
  if (target === undefined) return undefined;
  const patch = change.op === "amend" ? (content as Partial<Ticket>) : undefined;
  const existing = checkExistingTicketChange(target, {
    op: change.op,
    ticket_id: target.id,
    patch:
      patch === undefined ? undefined : { ...patch, allowed_paths: stringsOf(patch.allowed_paths) },
  });
  if (existing !== undefined) return existing;
  return change.op === "amend" ? uncodedUnrunnable(runnabilityOf(target, content)) : undefined;
}

/**
 * First governance refusal anywhere in the batch, formatted like the applier's
 * per-change reasons, or undefined. A check that throws on hostile input is
 * itself treated as a governance refusal (fail closed).
 */
export function governanceRefusalInBatch(
  tickets: Ticket[],
  changes: ReadonlyArray<WireChange>,
): string | undefined {
  for (const [index, change] of changes.entries()) {
    const where = `proposed_changes[${index}] (${change.op} ${quoteWorkerValue(change.target)})`;
    try {
      const refusal = changeGovernanceRefusal(change, tickets);
      if (refusal !== undefined) return `${where}: ${refusal}`;
    } catch (error) {
      return `${where}: không kiểm được guard governance (${String(error).slice(0, 120)})`;
    }
  }
  return undefined;
}
